import { getPostPurchaseRedirect } from "./postPurchaseRedirect";
import { UPSELL_V2_ENABLED, UPSELL_V2_SEQUENCES } from "../constants/upsellsV2";
import { REDIRECT_LINK } from "../constants/formConstants";
import { getPaywallContext } from "./paywallContext";
import {
  getUpsellV2Funnel,
  getUpsellV2Target,
  preserveUpsellV2Search,
} from "./upsellV2Flow";
export const UPSELL_V2_STORAGE_KEY = "upsellV2Chain";
const terminal = ["succeeded", "failed", "unknown", "skipped"];
const nonempty = (value) => typeof value === "string" && value.length > 0;
// Checkout recovery is necessary state, but its persisted URL must not become
// a click-ID store. During unresolved Google geography, fail closed for storage.
function withoutNativeClickStorage(session) {
  const consent = typeof window !== "undefined" && window.caConsent;
  if (!session || !(consent?.googleClickPassthrough ||
      (consent?.active && !consent.ready && consent.stapeScope?.version === 2))) return session;
  const params = new URLSearchParams(session.search);
  ["gclid", "dclid", "gclsrc", "gbraid", "wbraid", "_gl"].forEach(key => params.delete(key));
  const query = params.toString();
  return {...session, search: query ? `?${query}` : ""};
}
function isValid(session) {
  if (
    !session ||
    session.version !== 2 ||
    !nonempty(session.id) ||
    !["usd", "eur"].includes(session.currency) ||
    !nonempty(session.initialPaywall) ||
    typeof session.clientReferenceId !== "string" ||
    typeof session.search !== "string" ||
    session.search !== preserveUpsellV2Search(session.search) ||
    !getUpsellV2Funnel(session.initialPathname) ||
    getUpsellV2Funnel(session.initialPathname) !== session.funnel ||
    getPaywallContext(session.initialPathname).paywall !==
      session.initialPaywall ||
    !Number.isInteger(session.currentStep) ||
    session.currentStep < 0 ||
    session.currentStep > 3 ||
    !Array.isArray(session.steps) ||
    session.steps.length !== 3
  )
    return false;
  return session.steps.every((step, index) => {
    if (!step || step.productKey !== UPSELL_V2_SEQUENCES[session.funnel][index])
      return false;
    if (index < session.currentStep)
      return (
        terminal.includes(step.outcome) &&
        (step.outcome !== "succeeded" || nonempty(step.paymentIntentId))
      );
    if (index > session.currentStep) return step.outcome === "ready";
    return (
      step.outcome === "ready" ||
      (step.outcome === "submitted" && nonempty(step.attemptId))
    );
  });
}

// A singleton per document distinguishes a live request during React remounts from
// a submitted request left behind by reload. This state is UX, never entitlement.
export function createUpsellV2SessionStore({
  getStorage = () => window.sessionStorage,
  createId = () => window.crypto.randomUUID(),
} = {}) {
  let blocked = false;
  const activeAttempts = new Set();
  const listeners = new Set();
  const notify = () => listeners.forEach((listener) => listener());
  const fail = () => {
    blocked = true;
    notify();
    return null;
  };
  const read = () => {
    if (blocked) return null;
    try {
      const session = JSON.parse(getStorage().getItem(UPSELL_V2_STORAGE_KEY));
      return isValid(session) ? withoutNativeClickStorage(session) : null;
    } catch (_) {
      return fail();
    }
  };
  const write = (session) => {
    if (blocked) return null;
    try {
      getStorage().setItem(UPSELL_V2_STORAGE_KEY, JSON.stringify(withoutNativeClickStorage(session)));
    } catch (_) {
      return fail();
    }
    notify();
    return session;
  };
  const matches = (session, context) =>
    session &&
    context &&
    session.id === context.chainId &&
    session.currentStep === context.stepIndex &&
    session.currentStep < 3;
  const finish = (attempt, outcome, paymentIntentId) => {
    activeAttempts.delete(attempt?.attemptId);
    const session = read();
    if (!session) {
      notify();
      return null;
    }
    if (
      !matches(session, attempt) ||
      !["succeeded", "failed", "unknown"].includes(outcome)
    )
      return null;
    const step = session.steps[session.currentStep];
    if (step.outcome !== "submitted" || step.attemptId !== attempt.attemptId)
      return null;
    if (outcome === "succeeded" && !nonempty(paymentIntentId)) return null;
    step.outcome = outcome;
    if (nonempty(paymentIntentId)) step.paymentIntentId = paymentIntentId;
    session.currentStep += 1;
    return write(session);
  };
  return {
    read,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    start({
      enabled = UPSELL_V2_ENABLED,
      pathname,
      currency,
      search = "",
      clientReferenceId,
      legacyTarget,
      redirectLink = REDIRECT_LINK,
    }) {
      const funnel = getUpsellV2Funnel(pathname);
      if (!enabled || !funnel || !["usd", "eur"].includes(currency))
        return legacyTarget;
      try {
        const safeSearch = preserveUpsellV2Search(search);
        const session = {
          version: 2,
          id: createId(),
          initialPathname: pathname,
          initialPaywall: getPaywallContext(pathname).paywall,
          funnel,
          currency,
          search: safeSearch,
          clientReferenceId:
            clientReferenceId ||
            new URLSearchParams(safeSearch).get("client_reference_id") ||
            "",
          currentStep: 0,
          steps: UPSELL_V2_SEQUENCES[funnel].map((productKey) => ({
            productKey,
            outcome: "ready",
          })),
        };
        return isValid(session) && write(session)
          ? getUpsellV2Target(session, redirectLink)
          : getPostPurchaseRedirect(redirectLink, search);
      } catch (_) {
        fail();
        return getPostPurchaseRedirect(redirectLink, search);
      }
    },
    resume() {
      const session = read();
      if (!session) {
        notify();
        return null;
      }
      const step = session?.steps[session.currentStep];
      if (step?.outcome !== "submitted" || activeAttempts.has(step.attemptId))
        return session;
      return finish(
        {
          chainId: session.id,
          stepIndex: session.currentStep,
          attemptId: step.attemptId,
        },
        "unknown",
      );
    },
    begin(context) {
      const session = read();
      if (!session) {
        notify();
        return null;
      }
      if (
        !matches(session, context) ||
        session.steps[session.currentStep].outcome !== "ready"
      )
        return null;
      try {
        const attempt = {
          ...context,
          attemptId: createId(),
        };
        if (!nonempty(attempt.attemptId)) return fail();
        Object.assign(session.steps[session.currentStep], {
          outcome: "submitted",
          attemptId: attempt.attemptId,
        });
        activeAttempts.add(attempt.attemptId);
        if (!write(session)) {
          activeAttempts.delete(attempt.attemptId);
          return null;
        }
        return attempt;
      } catch (_) {
        return fail();
      }
    },
    finish,
    skip(context) {
      const session = read();
      if (!session) {
        notify();
        return null;
      }
      if (
        !matches(session, context) ||
        !["ready", "submitted"].includes(session.steps[session.currentStep].outcome)
      )
        return null;
      const step = session.steps[session.currentStep];
      activeAttempts.delete(step.attemptId);
      // Keep the attempt ID; skipping navigation does not cancel its charge.
      step.outcome = "skipped";
      session.currentStep += 1;
      return write(session);
    },
    credentials() {
      if (blocked) return null;
      try {
        const storage = getStorage();
        const customerId = storage.getItem("stripeCustomerId");
        const paymentMethodId = storage.getItem("stripePaymentMethodId");
        return nonempty(customerId) && nonempty(paymentMethodId)
          ? {
              customerId,
              paymentMethodId,
            }
          : null;
      } catch (_) {
        return fail();
      }
    },
  };
}
export const upsellV2Session = createUpsellV2SessionStore();
